import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Switch } from '../ui/switch';
import {
  LogOut,
  RefreshCw,
  DoorOpen,
  DoorClosed,
  X,
  Key,
  Trash2,
  Clock,
  ShieldCheck,
  UserPlus,
  Settings2,
  Bell,
  CheckSquare,
  FileText,
  Download,
  TrendingUp,
  BarChart3,
  CircleDollarSign,
  ReceiptText,
  Trophy,
  UsersRound,
  Phone,
  Mail,
  CreditCard,
} from 'lucide-react';

export function AdminHeader({ isSuperAdmin, user, kitchenState, openKitchen, setModal, setModalData, dailyReset, logout }) {
  const resetAction = () => {
    setModalData({ action: dailyReset, text: 'DELETE ALL ORDERS and reset counter?' });
    setModal('confirm');
  };

  if (isSuperAdmin) {
    return (
      <div className="bg-[#1B3A1A] text-white px-6 py-2.5 flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.2em] text-[#7D8B69] font-medium">Admin Panel</span>
        <div className="flex items-center gap-2">
          {kitchenState === 'closed' ? (
            <button onClick={openKitchen} className="px-3 py-1.5 rounded-lg border border-[#4A7C3F] text-[#7D8B69] text-[10px] font-bold uppercase hover:bg-[#4A7C3F] hover:text-white transition-colors flex items-center gap-1" data-testid="open-kitchen-btn"><DoorOpen size={12} /> Open</button>
          ) : (
            <button onClick={() => setModal('confirm-close')} className="px-3 py-1.5 rounded-lg border border-[#FF6B6B] text-[#FF6B6B] text-[10px] font-bold uppercase hover:bg-[#FF6B6B] hover:text-white transition-colors flex items-center gap-1" data-testid="close-kitchen-btn"><DoorClosed size={12} /> Close</button>
          )}
          <button onClick={resetAction} className="p-1.5 rounded-lg border border-[#B8860B]/50 text-[#B8860B] hover:bg-[#B8860B] hover:text-white transition-colors" data-testid="daily-reset-btn"><RefreshCw size={12} /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#1B3A1A] text-white px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-3"><div><span className="text-xs uppercase tracking-[0.2em] text-[#7D8B69] font-medium">Admin</span><h2 className="text-lg font-bold tracking-tight leading-none">{user.label}</h2></div></div>
      <div className="flex items-center gap-2">
        {kitchenState === 'closed' ? (
          <button onClick={openKitchen} className="px-4 py-2 rounded-lg border border-[#4A7C3F] text-[#7D8B69] text-xs font-bold uppercase hover:bg-[#4A7C3F] hover:text-white transition-colors flex items-center gap-1.5" data-testid="open-kitchen-btn"><DoorOpen size={14} /> Open Kitchen</button>
        ) : (
          <button onClick={() => setModal('confirm-close')} className="px-4 py-2 rounded-lg border border-[#FF6B6B] text-[#FF6B6B] text-xs font-bold uppercase hover:bg-[#FF6B6B] hover:text-white transition-colors flex items-center gap-1.5" data-testid="close-kitchen-btn"><DoorClosed size={14} /> Close</button>
        )}
        <button onClick={resetAction} className="p-2 rounded-lg border border-[#B8860B]/50 text-[#B8860B] hover:bg-[#B8860B] hover:text-white transition-colors" data-testid="daily-reset-btn"><RefreshCw size={14} /></button>
        <button onClick={logout} className="p-2 rounded-lg border border-white/20 hover:bg-white/10 transition-colors" data-testid="logout-btn"><LogOut size={16} /></button>
      </div>
    </div>
  );
}

export function AdminTabNav({ tabs, tab, onTabSelect }) {
  return (
    <div className="flex overflow-x-auto border-b border-[#7D8B69]/20 bg-white [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="admin-tab-nav">
      {tabs.map(t => (
        <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => onTabSelect(t.id)} className={`flex-none min-w-[112px] py-3.5 text-[11px] sm:min-w-[128px] sm:text-xs lg:flex-1 lg:min-w-0 font-bold uppercase tracking-wider transition-all whitespace-nowrap px-3 flex items-center justify-center gap-1.5 ${tab === t.id ? 'text-[#B8860B] border-b-2 border-[#B8860B] bg-[#FDFBF7]' : 'text-[#7D8B69] hover:text-[#1B3A1A] hover:bg-[#F3F0E6]'}`}>
          <t.icon size={14} strokeWidth={1.5} /> {t.label}
        </button>
      ))}
    </div>
  );
}

export function AdminCommsTab({ comms, setComms, api, notify }) {
  const saveComms = async () => {
    try { await api.put('/settings/communications', comms); notify('Communications settings saved'); }
    catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-comms-section">
      <div className="grid gap-4 lg:grid-cols-2">
        <CommsCard icon={<CreditCard size={16} />} title="Payment integrations" eyebrow="Checkout modes" testId="settings-payment-integrations-card">
          <div className="space-y-3">
            <ToggleRow
              label="Enable Stripe / Square integrations"
              detail="Turn off to show only Cash and Credit at checkout."
              checked={comms.payment_integrations_enabled !== false}
              onCheckedChange={checked => setComms(c => ({ ...c, payment_integrations_enabled: checked }))}
              testId="payment-integrations-enabled-toggle"
            />
            <p className="rounded-2xl bg-white px-4 py-3 text-xs leading-5 text-[#4A5D4E]" data-testid="payment-integrations-helper-text">Credit stays available as a simple recorded payment method when integrations are off.</p>
          </div>
        </CommsCard>

        <CommsCard icon={<Phone size={16} />} title="SMS notifications" eyebrow="Ready alerts" testId="settings-sms-card">
          <div className="space-y-3">
            <ToggleRow
              label="Enable SMS"
              detail="Allow order-ready text messages."
              checked={comms.sms_enabled || false}
              onCheckedChange={checked => setComms(c => ({ ...c, sms_enabled: checked }))}
              testId="sms-enabled-toggle"
            />
            <ToggleRow
              label="Auto-send ready alert"
              detail="Send when the kitchen marks an order ready."
              checked={comms.sms_auto_notify || false}
              onCheckedChange={checked => setComms(c => ({ ...c, sms_auto_notify: checked }))}
              testId="sms-auto-notify-toggle"
            />
            <div className="grid gap-2">
              <CommsInput value={comms.twilio_account_sid || ''} onChange={e => setComms(c => ({ ...c, twilio_account_sid: e.target.value }))} placeholder="Twilio Account SID" testId="twilio-sid" />
              <CommsInput value={comms.twilio_auth_token || ''} onChange={e => setComms(c => ({ ...c, twilio_auth_token: e.target.value }))} placeholder="Twilio Auth Token" type="password" testId="twilio-token" />
              <CommsInput value={comms.twilio_phone_number || ''} onChange={e => setComms(c => ({ ...c, twilio_phone_number: e.target.value }))} placeholder="Twilio Phone # (+1234567890)" testId="twilio-phone" />
            </div>
          </div>
        </CommsCard>

        <CommsCard icon={<Mail size={16} />} title="Email delivery" eyebrow="Report routing" testId="settings-email-card">
          <div className="space-y-3">
            <ToggleRow
              label="Enable Email"
              detail="Use the selected provider for reports."
              checked={comms.email_enabled || false}
              onCheckedChange={checked => setComms(c => ({ ...c, email_enabled: checked }))}
              testId="email-enabled-toggle"
            />
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="email-provider-options">
              <EmailProviderOption label="SendGrid" value="sendgrid" active={comms.email_provider === 'sendgrid'} onSelect={() => setComms(c => ({ ...c, email_provider: 'sendgrid' }))} testId="email-provider-sendgrid" />
              <EmailProviderOption label="Resend" value="resend" active={comms.email_provider === 'resend'} onSelect={() => setComms(c => ({ ...c, email_provider: 'resend' }))} testId="email-provider-resend" />
            </div>
            <div className="grid gap-2">
              {comms.email_provider === 'sendgrid' && <CommsInput value={comms.sendgrid_api_key || ''} onChange={e => setComms(c => ({ ...c, sendgrid_api_key: e.target.value }))} placeholder="SendGrid API Key" type="password" testId="sendgrid-key" />}
              {comms.email_provider === 'resend' && <CommsInput value={comms.resend_api_key || ''} onChange={e => setComms(c => ({ ...c, resend_api_key: e.target.value }))} placeholder="Resend API Key" type="password" testId="resend-key" />}
              <CommsInput value={comms.email_from || ''} onChange={e => setComms(c => ({ ...c, email_from: e.target.value }))} placeholder="From email address" testId="email-from" />
            </div>
          </div>
        </CommsCard>

        <CommsCard icon={<CreditCard size={16} />} title="Card payments" eyebrow="Checkout" testId="settings-stripe-card">
          <div className="space-y-3">
            <ToggleRow
              label="Enable Card Payments"
              detail="Enable Stripe-backed card checkout when integrations are on."
              checked={comms.stripe_enabled || false}
              onCheckedChange={checked => setComms(c => ({ ...c, stripe_enabled: checked }))}
              testId="stripe-enabled-toggle"
              disabled={comms.payment_integrations_enabled === false}
            />
            <div className={`grid gap-2 ${comms.payment_integrations_enabled === false ? 'opacity-50' : ''}`}>
              <CommsInput value={comms.stripe_publishable_key || ''} onChange={e => setComms(c => ({ ...c, stripe_publishable_key: e.target.value }))} placeholder="Stripe Publishable Key (pk_...)" testId="stripe-pk" disabled={comms.payment_integrations_enabled === false} />
              <CommsInput value={comms.stripe_secret_key || ''} onChange={e => setComms(c => ({ ...c, stripe_secret_key: e.target.value }))} placeholder="Stripe Secret Key (sk_...)" type="password" testId="stripe-sk" disabled={comms.payment_integrations_enabled === false} />
            </div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-[#7D8B69]" data-testid="stripe-helper-text">{comms.payment_integrations_enabled === false ? 'Integration disabled: checkout will show Credit as recorded payment only.' : 'Use test keys for service rehearsal.'}</p>
          </div>
        </CommsCard>

        <CommsCard icon={<CreditCard size={16} />} title="Square payments" eyebrow="Square" testId="settings-square-card">
          <div className="space-y-3">
            <ToggleRow
              label="Enable Square"
              detail="Use Square for card checkout or POS handoff."
              checked={comms.square_enabled || false}
              onCheckedChange={checked => setComms(c => ({ ...c, square_enabled: checked }))}
              testId="square-enabled-toggle"
              disabled={comms.payment_integrations_enabled === false}
            />
            <div className={`grid grid-cols-1 gap-2 sm:grid-cols-2 ${comms.payment_integrations_enabled === false ? 'opacity-50' : ''}`} data-testid="square-environment-options">
              <EmailProviderOption label="Sandbox" active={(comms.square_environment || 'sandbox') === 'sandbox'} onSelect={() => setComms(c => ({ ...c, square_environment: 'sandbox' }))} testId="square-env-sandbox" disabled={comms.payment_integrations_enabled === false || !comms.square_enabled} />
              <EmailProviderOption label="Production" active={comms.square_environment === 'production'} onSelect={() => setComms(c => ({ ...c, square_environment: 'production' }))} testId="square-env-production" disabled={comms.payment_integrations_enabled === false || !comms.square_enabled} />
            </div>
            <ToggleRow
              label="Square POS handoff"
              detail="Open Square POS app instead of in-browser card form."
              checked={comms.square_pos_mode || false}
              onCheckedChange={checked => setComms(c => ({ ...c, square_pos_mode: checked }))}
              testId="square-pos-mode-toggle"
              disabled={comms.payment_integrations_enabled === false || !comms.square_enabled}
            />
            <ToggleRow
              label="POS demo mode"
              detail="Append demo flag when testing Square POS handoff."
              checked={comms.square_pos_demo_mode || false}
              onCheckedChange={checked => setComms(c => ({ ...c, square_pos_demo_mode: checked }))}
              testId="square-pos-demo-toggle"
              disabled={comms.payment_integrations_enabled === false || !comms.square_enabled || !comms.square_pos_mode}
            />
            <div className={`grid gap-2 ${comms.payment_integrations_enabled === false ? 'opacity-50' : ''}`}>
              <CommsInput value={comms.square_application_id || ''} onChange={e => setComms(c => ({ ...c, square_application_id: e.target.value }))} placeholder="Square Application ID" testId="square-app-id" disabled={comms.payment_integrations_enabled === false || !comms.square_enabled} />
              <CommsInput value={comms.square_location_id || ''} onChange={e => setComms(c => ({ ...c, square_location_id: e.target.value }))} placeholder="Square Location ID" testId="square-location" disabled={comms.payment_integrations_enabled === false || !comms.square_enabled} />
              <CommsInput value={comms.square_access_token || ''} onChange={e => setComms(c => ({ ...c, square_access_token: e.target.value }))} placeholder="Square Access Token" type="password" testId="square-token" disabled={comms.payment_integrations_enabled === false || !comms.square_enabled} />
              <CommsInput value={comms.square_callback_url || ''} onChange={e => setComms(c => ({ ...c, square_callback_url: e.target.value }))} placeholder="Square POS callback URL" testId="square-callback-url" disabled={comms.payment_integrations_enabled === false || !comms.square_enabled} />
            </div>
          </div>
        </CommsCard>

        <CommsCard icon={<FileText size={16} />} title="End-of-day report" eyebrow="Closeout" testId="settings-report-card">
          <div className="space-y-3">
            <ToggleRow
              label="Auto-email at kitchen close"
              detail="Send the report after closeout is completed."
              checked={comms.auto_email_report || false}
              onCheckedChange={checked => setComms(c => ({ ...c, auto_email_report: checked }))}
              testId="auto-email-report-toggle"
            />
            <CommsInput value={comms.report_email_to || ''} onChange={e => setComms(c => ({ ...c, report_email_to: e.target.value }))} placeholder="Send report to (email)" testId="report-email" />
          </div>
        </CommsCard>
      </div>

      <button onClick={saveComms} className="min-h-12 w-full rounded-xl bg-[#5C6B5A] px-8 font-mono text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-colors duration-200 hover:bg-[#4A5748] active:scale-[0.99]" data-testid="save-comms-btn">Save Communications Settings</button>
    </div>
  );
}

function CommsCard({ icon, title, eyebrow, testId, children }) {
  return (
    <section className="rounded-2xl border border-stone-200/80 bg-[#FDFBF7] p-4 shadow-[0_8px_24px_rgba(27,58,26,0.04)] transition-shadow duration-200 hover:shadow-[0_12px_32px_rgba(27,58,26,0.07)] sm:p-5" data-testid={testId}>
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#E9EFE7] text-[#1B3A1A]">{icon}</div>
        <div>
          <div className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#7D8B69]">{eyebrow}</div>
          <h4 className="mt-1 font-heading text-lg font-bold leading-tight text-[#1B3A1A]">{title}</h4>
        </div>
      </div>
      {children}
    </section>
  );
}

function ToggleRow({ label, detail, checked, onCheckedChange, testId, disabled = false }) {
  return (
    <div className={`flex min-h-14 items-center justify-between gap-4 rounded-2xl bg-white px-4 py-3 shadow-[inset_0_0_0_1px_rgba(125,139,105,0.12)] ${disabled ? 'opacity-55' : ''}`} data-testid={`${testId}-row`}>
      <div>
        <div className="font-heading text-sm font-bold text-[#1B3A1A]">{label}</div>
        <p className="mt-0.5 text-xs leading-5 text-[#4A5D4E]">{detail}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} className="h-7 w-12 data-[state=checked]:bg-[#5C6B5A] data-[state=unchecked]:bg-stone-300 [&>span]:h-6 [&>span]:w-6 [&>span]:data-[state=checked]:translate-x-5" data-testid={testId} aria-label={label} />
    </div>
  );
}

function EmailProviderOption({ label, active, onSelect, testId, disabled = false }) {
  return (
    <button type="button" onClick={onSelect} disabled={disabled} className={`min-h-12 rounded-xl border px-4 font-mono text-xs font-bold uppercase tracking-wider transition-all duration-200 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-55 ${active ? 'border-[#5C6B5A] bg-[#E9EFE7] text-[#1B3A1A]' : 'border-stone-200 bg-white text-[#7D8B69] hover:bg-[#F4F1EB]'}`} data-testid={testId} aria-pressed={active}>
      {label}
    </button>
  );
}

function CommsInput({ testId, className = '', ...props }) {
  return <input className={`min-h-12 w-full rounded-xl border border-stone-200 bg-white px-4 text-sm transition-all duration-200 focus:border-[#5C6B5A] focus:ring-2 focus:ring-[#5C6B5A]/20 ${className}`} data-testid={testId} {...props} />;
}

export function AdminSettingsTab({ comms, setComms, api, notify, thresholds, setThresholds, ServiceTasksPanelComponent }) {
  const saveThresholds = async () => {
    await api.put('/settings/thresholds', thresholds);
    notify('Timer thresholds saved');
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 animate-slide-in" data-testid="admin-settings-section">
      <SettingsOverview />
      <Accordion type="multiple" defaultValue={["operations"]} className="space-y-6" data-testid="settings-collapsible-sections">
        <SettingsAccordionItem id="operations" icon={<CheckSquare size={18} />} eyebrow="Service rhythm" title="Operations" description="Opening and closing task lists for repeatable service setup.">
          <div className="rounded-2xl bg-[#FDFBF7] p-4" data-testid="settings-operations-content">
            <ServiceTasksPanelComponent variant="soft" />
          </div>
        </SettingsAccordionItem>

        <SettingsAccordionItem id="communications" icon={<Bell size={18} />} eyebrow="Customer touchpoints" title="Communications" description="SMS, email, payment, and report delivery configuration.">
          <div data-testid="settings-communications-content">
            <AdminCommsTab comms={comms} setComms={setComms} api={api} notify={notify} variant="soft" />
          </div>
        </SettingsAccordionItem>

        <SettingsAccordionItem id="timers" icon={<Clock size={18} />} eyebrow="Ticket pressure" title="Ticket Timer Thresholds" description="Tune warning, urgent, and critical timing for cooking and pickup flow.">
          <TimerThresholdSettings thresholds={thresholds} setThresholds={setThresholds} saveThresholds={saveThresholds} />
        </SettingsAccordionItem>
      </Accordion>
    </div>
  );
}

function SettingsOverview() {
  const stats = [
    { label: 'Ops', value: 'Tasks', testId: 'settings-overview-ops' },
    { label: 'Comms', value: 'Notify', testId: 'settings-overview-comms' },
    { label: 'Timers', value: 'Alerts', testId: 'settings-overview-timers' },
  ];

  return (
    <section className="relative overflow-hidden rounded-3xl border border-stone-200/60 bg-gradient-to-br from-[#F4F1EB] to-[#F9F8F6] shadow-[0_14px_40px_rgba(27,58,26,0.08)]" data-testid="settings-overview-panel">
      <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-[#E8F0E4] to-transparent" />
      <div className="relative grid gap-6 p-6 sm:p-7 lg:grid-cols-[1.2fr_1fr] lg:items-end">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#7D8B69]/20 bg-white px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C6B5A] shadow-sm" data-testid="settings-overview-kicker"><Settings2 size={12} /> Service Settings</div>
          <h2 className="font-heading text-3xl font-bold leading-tight text-[#1B3A1A] sm:text-4xl">Keep service behavior in one calm control room.</h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-[#4A5D4E]">Manage operational checklists, customer communications, and ticket timing from one organized place.</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {stats.map(stat => <div key={stat.testId} className="rounded-2xl border border-stone-200/80 bg-white/75 p-4 shadow-sm backdrop-blur-sm" data-testid={stat.testId}><div className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#7D8B69]">{stat.label}</div><div className="mt-1 font-mono text-lg font-bold text-[#1B3A1A]">{stat.value}</div></div>)}
        </div>
      </div>
    </section>
  );
}

function SettingsAccordionItem({ id, icon, eyebrow, title, description, children }) {
  return (
    <AccordionItem value={id} className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm transition-shadow duration-200 hover:shadow-md" data-testid={`settings-${id}-section`}>
      <AccordionTrigger className="min-h-16 px-5 py-5 text-left no-underline transition-colors duration-200 hover:bg-[#FDFBF7] hover:no-underline focus:outline-none focus:ring-2 focus:ring-[#5C6B5A]/50 sm:px-6 [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-[#5C6B5A]" data-testid={`settings-${id}-toggle`}>
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#E9EFE7] text-[#1B3A1A]">{icon}</div>
          <div>
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#7D8B69]">{eyebrow}</div>
            <h3 className="mt-1 font-heading text-xl font-bold leading-tight text-[#1B3A1A]">{title}</h3>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-[#4A5D4E]">{description}</p>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="border-t border-[#7D8B69]/10 p-5 pt-5 sm:p-6 sm:pt-6" data-testid={`settings-${id}-content`}>{children}</AccordionContent>
    </AccordionItem>
  );
}

function TimerThresholdSettings({ thresholds, setThresholds, saveThresholds }) {
  return (
    <div className="space-y-4" data-testid="settings-timer-thresholds-panel">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[[ 'new_warning','New Warn' ],[ 'new_urgent','New Urgent' ],[ 'new_critical','New Critical' ],[ 'pickup_warning','Pickup Warn' ],[ 'pickup_urgent','Pickup Urgent' ],[ 'pickup_critical','Pickup Critical' ]].map(([k,l]) => <SettingsThresholdField key={k} id={k} label={l} value={thresholds[k]} setThresholds={setThresholds} />)}
      </div>
      <button onClick={saveThresholds} className="h-12 w-full rounded-xl bg-[#1B3A1A] font-mono text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-all duration-200 hover:bg-[#2C4C2B] active:scale-[0.99]" data-testid="settings-save-thresholds-btn">Save Timer Thresholds</button>
    </div>
  );
}

function SettingsThresholdField({ id, label, value, setThresholds }) {
  return (
    <label className="block rounded-2xl bg-[#FDFBF7] p-3" data-testid={`settings-threshold-field-${id}`}>
      <span className="mb-1.5 flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#7D8B69]"><Clock size={11} /> {label}</span>
      <input type="number" value={value} onChange={e => setThresholds(t => ({ ...t, [id]: parseInt(e.target.value) || 0 }))} className="h-12 w-full rounded-xl border border-[#7D8B69]/25 bg-white px-4 text-right font-mono text-base font-bold transition-all duration-200 focus:border-[#1B3A1A] focus:ring-2 focus:ring-[#1B3A1A]/10" data-testid={`settings-threshold-${id}`} />
    </label>
  );
}

export function AdminStaffTab({ pinForm, setPinForm, createPin, pins, setModalData, setModal, deletePin }) {
  const roleCounts = pins.reduce((acc, pin) => ({ ...acc, [pin.role]: (acc[pin.role] || 0) + 1 }), {});

  return (
    <div className="space-y-6 animate-slide-in" data-testid="admin-staff-section">
      <StaffOverview total={pins.length} roleCounts={roleCounts} />
      <section className="rounded-3xl border border-[#7D8B69]/15 bg-white p-5 shadow-[0_12px_35px_rgba(27,58,26,0.06)] sm:p-6" data-testid="create-staff-pin-form">
        <StaffSectionHeader icon={<UserPlus size={18} />} eyebrow="Access setup" title="Create staff PIN" description="Give each person the right view for service without sharing owner access." testId="staff-create-header" />
        <div className="mt-5 space-y-4">
          <input value={pinForm.label} onChange={e => setPinForm(f => ({ ...f, label: e.target.value }))} placeholder="Staff name" className="h-12 w-full rounded-xl border border-[#7D8B69]/25 bg-[#FDFBF7] px-4 text-sm transition-all duration-200 focus:border-[#1B3A1A] focus:ring-2 focus:ring-[#1B3A1A]/10" data-testid="staff-pin-label-input" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input value={pinForm.pin} onChange={e => setPinForm(f => ({ ...f, pin: e.target.value }))} placeholder="PIN (4+)" type="password" className="h-12 rounded-xl border border-[#7D8B69]/25 bg-[#FDFBF7] px-4 text-sm transition-all duration-200 focus:border-[#1B3A1A] focus:ring-2 focus:ring-[#1B3A1A]/10" data-testid="staff-pin-input" />
            <select value={pinForm.role} onChange={e => setPinForm(f => ({ ...f, role: e.target.value }))} className="h-12 rounded-xl border border-[#7D8B69]/25 bg-[#FDFBF7] px-4 text-sm transition-all duration-200 focus:border-[#1B3A1A] focus:ring-2 focus:ring-[#1B3A1A]/10" data-testid="staff-role-select"><option value="order_taker">Order Taker</option><option value="cook">Cook</option><option value="admin">Admin</option></select>
          </div>
          <button onClick={createPin} className="h-12 w-full rounded-xl bg-[#1B3A1A] font-mono text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-all duration-200 hover:bg-[#2C4C2B] active:scale-[0.99]" data-testid="create-staff-pin-btn"><Key className="inline mr-2" size={16} /> CREATE PIN</button>
        </div>
      </section>

      <section className="rounded-3xl border border-[#7D8B69]/15 bg-white p-5 shadow-[0_12px_35px_rgba(27,58,26,0.06)] sm:p-6" data-testid="staff-pin-list">
        <StaffSectionHeader icon={<ShieldCheck size={18} />} eyebrow="Current access" title="Staff PINs" description="Reset or remove access as staffing changes between events." testId="staff-list-header" />
        <div className="mt-5 space-y-3">
          {pins.map(p => <StaffPinRow key={p.id} pin={p} setModalData={setModalData} setModal={setModal} deletePin={deletePin} />)}
          {pins.length === 0 && <p className="rounded-2xl border border-dashed border-[#7D8B69]/25 bg-[#FDFBF7] p-6 text-center font-mono text-xs uppercase tracking-wider text-[#7D8B69]" data-testid="staff-empty-message">No staff PINs yet</p>}
        </div>
      </section>
    </div>
  );
}

function StaffOverview({ total, roleCounts }) {
  const stats = [
    { label: 'Staff', value: total, testId: 'staff-overview-total' },
    { label: 'Servers', value: roleCounts.order_taker || 0, testId: 'staff-overview-servers' },
    { label: 'Cooks', value: roleCounts.cook || 0, testId: 'staff-overview-cooks' },
    { label: 'Admins', value: roleCounts.admin || 0, testId: 'staff-overview-admins' },
  ];

  return (
    <section className="relative overflow-hidden rounded-3xl bg-[#1B3A1A] text-white shadow-[0_18px_45px_rgba(27,58,26,0.18)]" data-testid="staff-overview-panel">
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#B8860B]/20 to-transparent" />
      <div className="relative grid gap-6 p-6 sm:p-7 lg:grid-cols-[1.2fr_1fr] lg:items-end">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#F3F0E6]" data-testid="staff-overview-kicker"><ShieldCheck size={12} /> Team Access</div>
          <h2 className="font-heading text-3xl font-bold leading-tight sm:text-4xl">Give every role a clean path into service.</h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-[#F3F0E6]/85">Create PINs, reset access, and tune ticket timing without exposing owner-level controls.</p>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-2">
          {stats.map(stat => <div key={stat.testId} className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm" data-testid={stat.testId}><div className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#D4CFC5]">{stat.label}</div><div className="mt-1 font-mono text-2xl font-bold text-white">{stat.value}</div></div>)}
        </div>
      </div>
    </section>
  );
}

function StaffPinRow({ pin, setModalData, setModal, deletePin }) {
  return (
    <div className="grid grid-cols-1 gap-4 rounded-2xl border border-[#EFECE6] bg-[#FDFBF7] p-4 transition-all duration-200 hover:bg-white hover:shadow-[0_8px_25px_rgba(27,58,26,0.06)] sm:grid-cols-[1fr_auto] sm:items-center" data-testid={`staff-pin-${pin.id}`}>
      <div className="min-w-0">
        <div className="font-heading text-lg font-bold text-[#1B3A1A]">{pin.label}</div>
        <RoleBadge role={pin.role} />
      </div>
      <div className="flex gap-2 sm:justify-end">
        <button onClick={() => { setModalData({ pinId: pin.id, pinLabel: pin.label, newPin: '' }); setModal('reset-pin'); }} className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#D9B66F] text-[#B8860B] transition-all duration-200 hover:bg-[#FFF8E1] active:scale-95" data-testid={`reset-pin-${pin.id}`} aria-label={`Reset ${pin.label} PIN`}><Key size={16} /></button>
        <button onClick={() => deletePin(pin.id)} className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#F4B4B4] text-[#C43030] transition-all duration-200 hover:bg-[#FFF3F3] active:scale-95" data-testid={`delete-pin-${pin.id}`} aria-label={`Delete ${pin.label} PIN`}><Trash2 size={16} /></button>
      </div>
    </div>
  );
}

function RoleBadge({ role }) {
  const styles = {
    admin: 'bg-[#E8F0E4] text-[#1B3A1A]',
    cook: 'bg-[#FFF8E1] text-[#8A6500]',
    order_taker: 'bg-[#F3F0E6] text-[#4A5D4E]',
  };
  return <span className={`mt-2 inline-flex rounded-full px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider ${styles[role] || styles.order_taker}`} data-testid={`staff-role-badge-${role}`}>{role.replace('_', ' ')}</span>;
}

function StaffSectionHeader({ icon, eyebrow, title, description, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#E8F0E4] text-[#1B3A1A]">{icon}</div>
      <div>
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#7D8B69]">{eyebrow}</div>
        <h3 className="mt-1 font-heading text-xl font-bold leading-tight text-[#1B3A1A]">{title}</h3>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-[#4A5D4E]">{description}</p>
      </div>
    </div>
  );
}

export function AdminInsightsTab({ analytics, generateReport, downloadCSV, prepEstimate, popular, report }) {
  const today = analytics?.today || { total_orders: 0, revenue: 0, avg_order_value: 0, avg_cook_time: 0 };
  const customerCount = analytics?.all_time?.unique_customers || 0;
  const popularPreview = analytics?.all_time?.popular_items || [];

  return (
    <div className="space-y-6 animate-slide-in" data-testid="admin-insights-section">
      <InsightsOverview today={today} customerCount={customerCount} />
      <ReportActions generateReport={generateReport} downloadCSV={downloadCSV} />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_1fr]">
        <TodayPerformance analytics={analytics} popularPreview={popularPreview} />
        <PrepEstimateCard prepEstimate={prepEstimate} />
      </div>
      <PopularItemsCard popular={popular} />
      <ReportPreview report={report} />
    </div>
  );
}

function InsightsOverview({ today, customerCount }) {
  const stats = [
    { label: 'Orders', value: today.total_orders, testId: 'insights-overview-orders' },
    { label: 'Revenue', value: `$${today.revenue.toFixed(2)}`, testId: 'insights-overview-revenue' },
    { label: 'Avg order', value: `$${today.avg_order_value.toFixed(2)}`, testId: 'insights-overview-average-order' },
    { label: 'Avg cook', value: `${today.avg_cook_time}m`, testId: 'insights-overview-average-cook' },
  ];

  return (
    <section className="relative overflow-hidden rounded-3xl bg-[#1B3A1A] text-white shadow-[0_18px_45px_rgba(27,58,26,0.18)]" data-testid="insights-overview-panel">
      <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-[#B8860B]/20 to-transparent" />
      <div className="relative grid gap-6 p-6 sm:p-7 lg:grid-cols-[1.18fr_1fr] lg:items-end">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#F3F0E6]" data-testid="insights-overview-kicker"><BarChart3 size={12} /> Data & Closeout</div>
          <h2 className="font-heading text-3xl font-bold leading-tight sm:text-4xl">Read the service, close the day cleanly.</h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-[#F3F0E6]/85">Review sales, prep pressure, popular items, and export reports from one calm dashboard.</p>
          <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-[#F3F0E6]" data-testid="insights-customer-count"><UsersRound size={11} /> {customerCount} customers</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {stats.map(stat => <InsightStatCard key={stat.testId} {...stat} />)}
        </div>
      </div>
    </section>
  );
}

function InsightStatCard({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm" data-testid={testId}>
      <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#D4CFC5]">{label}</div>
      <div className="mt-1 font-mono text-2xl font-bold text-white">{value}</div>
    </div>
  );
}

function ReportActions({ generateReport, downloadCSV }) {
  return (
    <section className="rounded-3xl border border-[#7D8B69]/15 bg-white p-5 shadow-[0_12px_35px_rgba(27,58,26,0.06)] sm:p-6" data-testid="report-actions-panel">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <InsightsSectionHeader icon={<ReceiptText size={18} />} eyebrow="Closeout exports" title="Reports & downloads" description="Generate the day’s P&L, export CSV, or open a print-ready PDF." testId="report-actions-header" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:min-w-[520px]">
          <button onClick={generateReport} className="h-12 rounded-xl bg-[#1B3A1A] px-5 font-mono text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-all duration-200 hover:bg-[#2C4C2B] active:scale-[0.98]" data-testid="generate-report-btn"><FileText className="inline mr-1" size={15} />Report</button>
          <button onClick={downloadCSV} className="h-12 rounded-xl border border-[#4A7C3F] px-5 font-mono text-xs font-bold uppercase tracking-wider text-[#4A7C3F] transition-all duration-200 hover:bg-[#4A7C3F] hover:text-white active:scale-[0.98]" data-testid="download-report-csv-btn"><Download size={15} className="inline mr-1" />CSV</button>
          <button onClick={() => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/report/pdf`, '_blank'); }} className="h-12 rounded-xl border border-[#B8860B] px-5 font-mono text-xs font-bold uppercase tracking-wider text-[#B8860B] transition-all duration-200 hover:bg-[#B8860B] hover:text-white active:scale-[0.98]" data-testid="download-pdf-btn"><FileText size={15} className="inline mr-1" />PDF P&L</button>
        </div>
      </div>
    </section>
  );
}

function TodayPerformance({ analytics, popularPreview }) {
  if (!analytics) {
    return (
      <section className="rounded-3xl border border-dashed border-[#7D8B69]/25 bg-white p-6 text-center font-mono text-xs uppercase tracking-wider text-[#7D8B69] shadow-[0_12px_35px_rgba(27,58,26,0.06)]" data-testid="today-performance-empty">
        Generate or load analytics to see today’s performance
      </section>
    );
  }

  return (
    <section className="rounded-3xl border border-[#7D8B69]/15 bg-white p-5 shadow-[0_12px_35px_rgba(27,58,26,0.06)] sm:p-6" data-testid="today-performance-panel">
      <InsightsSectionHeader icon={<CircleDollarSign size={18} />} eyebrow="Today" title="Service performance" description="Orders, revenue, cook time, and quick top sellers for the current service." testId="today-performance-header" />
      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="today-performance-stats">
        <SoftMetric label="Orders" value={analytics.today.total_orders} testId="today-orders-metric" />
        <SoftMetric label="Revenue" value={`$${analytics.today.revenue.toFixed(2)}`} testId="today-revenue-metric" />
        <SoftMetric label="Avg order" value={`$${analytics.today.avg_order_value.toFixed(2)}`} testId="today-average-order-metric" />
        <SoftMetric label="Avg cook" value={`${analytics.today.avg_cook_time}m`} testId="today-average-cook-metric" />
      </div>
      {popularPreview.length > 0 && <div className="mt-5 space-y-2" data-testid="today-top-items-list"><div className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#7D8B69]">Quick top sellers</div>{popularPreview.slice(0, 5).map((item, idx) => <div key={`today-popular-${item.name}-${idx}`} className="flex items-center justify-between rounded-2xl bg-[#FDFBF7] px-4 py-3 font-mono text-xs" data-testid={`today-popular-item-${idx}`}><span className="font-bold text-[#1B3A1A]">{item.name}</span><span className="text-[#7D8B69]">{item.qty} sold</span></div>)}</div>}
    </section>
  );
}

function PrepEstimateCard({ prepEstimate }) {
  if (!prepEstimate?.items?.length) return null;
  const visibleItems = prepEstimate.items.filter(item => item.servings > 0);
  return (
    <section className="rounded-3xl border border-[#7D8B69]/15 bg-white p-5 shadow-[0_12px_35px_rgba(27,58,26,0.06)] sm:p-6" data-testid="prep-estimate-section">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <InsightsSectionHeader icon={<Clock size={18} />} eyebrow="Prep pressure" title="Prep time estimate" description="A quick read on kitchen effort based on current active servings." testId="prep-estimate-header" />
        <span className="inline-flex w-fit rounded-full bg-[#E8F0E4] px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-wider text-[#1B3A1A]" data-testid="prep-estimate-total">{prepEstimate.total_minutes} min · {prepEstimate.total_hours} hrs</span>
      </div>
      <div className="mt-5 space-y-2" data-testid="prep-estimate-list">
        {visibleItems.map((item, idx) => <div key={`prep-estimate-${item.name}-${idx}`} className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-2xl bg-[#FDFBF7] px-4 py-3 transition-colors duration-200 hover:bg-[#F3F0E6]" data-testid={`prep-estimate-item-${idx}`}><span className="font-heading text-sm font-bold text-[#1B3A1A]">{item.name} <span className="font-mono text-[10px] uppercase text-[#7D8B69]">({item.servings} srv)</span></span><span className="font-mono text-xs font-bold text-[#7D8B69]">{item.total_minutes} min</span></div>)}
      </div>
    </section>
  );
}

function PopularItemsCard({ popular }) {
  if (!popular?.items?.length) return null;
  return (
    <section className="rounded-3xl border border-[#7D8B69]/15 bg-white p-5 shadow-[0_12px_35px_rgba(27,58,26,0.06)] sm:p-6" data-testid="popular-analytics-section">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <InsightsSectionHeader icon={<Trophy size={18} />} eyebrow="All time" title="Popular items" description="See which items drive volume and when demand tends to peak." testId="popular-analytics-header" />
        {popular.overall_peak_hour !== null && <span className="inline-flex w-fit rounded-full bg-[#FFF8E1] px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-wider text-[#8A6500]" data-testid="popular-peak-hour">Busiest {popular.overall_peak_hour}:00</span>}
      </div>
      <div className="mt-5 overflow-hidden rounded-2xl bg-[#FDFBF7]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="popular-items-table">
            <thead className="font-mono text-[10px] uppercase tracking-wider text-[#7D8B69]"><tr className="border-b border-[#7D8B69]/20"><th className="p-3 text-left">Rank</th><th className="p-3 text-left">Item</th><th className="p-3 text-right">Sold</th><th className="p-3 text-right">Revenue</th><th className="p-3 text-left">Peak</th></tr></thead>
            <tbody>
              {popular.items.slice(0, 10).map((item, idx) => <tr key={`popular-${item.name}-${idx}`} className="border-b border-[#7D8B69]/10 transition-colors duration-200 hover:bg-[#F3F0E6]" data-testid={`popular-item-row-${idx}`}><td className="p-3 font-mono text-xs font-bold text-[#7D8B69]">#{idx + 1}</td><td className="p-3 font-heading font-bold text-[#1B3A1A]">{item.name}</td><td className="p-3 text-right font-mono text-xs">{item.total_qty}</td><td className="p-3 text-right font-mono text-xs text-[#4A7C3F]">${item.total_revenue.toFixed(0)}</td><td className="p-3 font-mono text-[10px] uppercase text-[#B8860B]">{item.peak_hour_label || '—'}</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function ReportPreview({ report }) {
  if (!report) return null;
  return (
    <section className="rounded-3xl border border-[#7D8B69]/15 bg-white p-5 shadow-[0_12px_35px_rgba(27,58,26,0.06)] sm:p-6" data-testid="report-preview-panel">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <InsightsSectionHeader icon={<FileText size={18} />} eyebrow="Generated report" title={report.date} description="Preview the P&L summary before printing or downloading." testId="report-preview-header" />
        <button onClick={() => window.print()} className="h-12 rounded-xl border border-[#7D8B69]/25 px-5 font-mono text-xs font-bold uppercase tracking-wider text-[#1B3A1A] transition-all duration-200 hover:bg-[#F3F0E6] active:scale-[0.98]" data-testid="print-report-btn">Print</button>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SoftMetric label="Orders" value={report.orders.total} testId="report-orders-metric" />
        <SoftMetric label="Completed" value={report.orders.completed} testId="report-completed-metric" />
        <SoftMetric label="Revenue" value={`$${report.revenue.total.toFixed(2)}`} testId="report-revenue-metric" />
      </div>
      <div className="mt-5 space-y-2" data-testid="report-items-sold-list">
        {Object.entries(report.items_sold).map(([name, data]) => <div key={name} className="grid grid-cols-[1fr_80px] items-center gap-3 rounded-2xl bg-[#FDFBF7] px-4 py-3 font-mono text-xs" data-testid={`report-item-${name}`}><span className="font-bold text-[#1B3A1A]">{name}</span><span className="text-right text-[#7D8B69]">{data.qty}x ${data.revenue.toFixed(2)}</span><div className="col-span-2 h-2 overflow-hidden rounded-full bg-white"><div className="h-full rounded-full bg-[#1B3A1A]" style={{ width: `${data.pct}%` }} /></div></div>)}
      </div>
    </section>
  );
}

function SoftMetric({ label, value, testId }) {
  return <div className="rounded-2xl bg-[#FDFBF7] p-4" data-testid={testId}><div className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#7D8B69]">{label}</div><div className="mt-1 font-mono text-2xl font-bold text-[#1B3A1A]">{value}</div></div>;
}

function InsightsSectionHeader({ icon, eyebrow, title, description, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#E9EFE7] text-[#1B3A1A]">{icon}</div>
      <div>
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#7D8B69]">{eyebrow}</div>
        <h3 className="mt-1 font-heading text-xl font-bold leading-tight text-[#1B3A1A]">{title}</h3>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-[#4A5D4E]">{description}</p>
      </div>
    </div>
  );
}
